import { useState, useEffect, useRef } from 'react'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { branding } from '../config/edition'

const DOCS_CSS = `
.docs *{box-sizing:border-box;margin:0;padding:0}
.docs{
  --bg:#fafafa;
  --surface:#ffffff;
  --border:#e4e4e7;
  --border-soft:#f0f0f2;
  --ink:#18181b;
  --muted:#71717a;
  --faint:#a1a1aa;
  --accent:#2563eb;
  --accent-bg:#eff6ff;
  --sans:ui-sans-serif,system-ui,-apple-system,sans-serif;
  --mono:ui-monospace,'Cascadia Code','Fira Code',monospace;
  --code-bg:#18181b;
  --code-fg:#e4e4e7;
  --code-comment:#71717a;
  font-family:var(--sans);
  font-size:15px;
  line-height:1.6;
  color:var(--ink);
  background:var(--bg);
  min-height:100vh;
}
@media(prefers-color-scheme:dark){
  .docs{
    --bg:#09090b;
    --surface:#111113;
    --border:#27272a;
    --border-soft:#1c1c1f;
    --ink:#fafafa;
    --muted:#a1a1aa;
    --faint:#52525b;
    --accent:#60a5fa;
    --accent-bg:#1e3a5f20;
    --code-bg:#0d0d0f;
    --code-fg:#d4d4d8;
    --code-comment:#52525b;
  }
}
.docs a{color:var(--accent);text-decoration:none}
.docs a:hover{text-decoration:underline}

/* nav */
.d-nav{
  position:sticky;top:0;z-index:40;
  border-bottom:1px solid var(--border);
  background:color-mix(in srgb,var(--bg) 85%,transparent);
  backdrop-filter:blur(12px);
}
.d-nav-inner{
  max-width:1080px;margin:0 auto;
  display:flex;align-items:center;justify-content:space-between;
  padding:0 24px;height:52px;
}
.d-nav-brand{font-size:14px;font-weight:600;color:var(--ink);letter-spacing:-0.01em}
.d-nav-links{display:flex;gap:20px;font-size:13px;color:var(--muted)}
.d-nav-links a{color:var(--muted)}
.d-nav-links a:hover{color:var(--ink);text-decoration:none}

/* layout */
.d-wrap{max-width:1080px;margin:0 auto;padding:40px 24px 80px;display:flex;gap:40px}
.d-sidebar{width:200px;flex-shrink:0}
.d-sidebar-sticky{position:sticky;top:72px}
.d-content{flex:1;min-width:0}

/* sidebar */
.d-nav-group{margin-bottom:24px}
.d-nav-label{font-size:11px;font-weight:600;letter-spacing:0.07em;text-transform:uppercase;color:var(--faint);margin-bottom:6px;padding:0 8px}
.d-nav-item{display:block;padding:5px 8px;border-radius:6px;font-size:13px;color:var(--muted);cursor:pointer;line-height:1.4}
.d-nav-item:hover{color:var(--ink);background:var(--border-soft);text-decoration:none}
.d-nav-item.active{color:var(--accent);background:var(--accent-bg)}

/* page header */
.d-page-header{margin-bottom:36px;padding-bottom:24px;border-bottom:1px solid var(--border)}
.d-page-header h1{font-size:26px;font-weight:700;letter-spacing:-0.02em;line-height:1.2;margin-bottom:8px}
.d-page-header p{color:var(--muted);font-size:15px;max-width:560px}

/* sections */
.d-section{margin-bottom:40px}
.d-section h2{font-size:18px;font-weight:600;letter-spacing:-0.01em;margin-bottom:12px;padding-bottom:8px;border-bottom:1px solid var(--border-soft);display:flex;align-items:center;gap:6px}
.d-section h3{font-size:14px;font-weight:600;margin:20px 0 8px;color:var(--ink)}

/* anchor link button next to headings */
.d-h-anchor{opacity:0;background:none;border:none;cursor:pointer;color:var(--faint);padding:2px 5px;border-radius:4px;font-size:13px;font-family:var(--mono);line-height:1;transition:opacity 0.15s,color 0.15s;flex-shrink:0}
.d-section h2:hover .d-h-anchor,.d-h-anchor:focus{opacity:1}
.d-h-anchor:hover{color:var(--accent)}

/* copy-page button */
.d-copy-page{margin-top:10px;display:inline-flex;align-items:center;gap:6px;border:1px solid var(--border);background:var(--surface);color:var(--muted);border-radius:6px;padding:5px 10px;font-size:12px;cursor:pointer;transition:color 0.15s,border-color 0.15s}
.d-copy-page:hover{color:var(--ink);border-color:var(--accent)}
.d-section p{color:var(--muted);margin-bottom:12px;line-height:1.7}
.d-section ul,.d-section ol{color:var(--muted);padding-left:20px;margin-bottom:12px;line-height:1.8}
.d-section li{margin-bottom:2px}

/* callouts */
.d-note{
  display:flex;gap:10px;align-items:flex-start;
  border:1px solid var(--border);
  border-left:3px solid var(--accent);
  border-radius:6px;
  padding:10px 12px;
  margin-bottom:14px;
  font-size:13px;
  color:var(--muted);
  background:var(--accent-bg);
}
.d-warn{
  display:flex;gap:10px;align-items:flex-start;
  border:1px solid var(--border);
  border-left:3px solid #f59e0b;
  border-radius:6px;
  padding:10px 12px;
  margin-bottom:14px;
  font-size:13px;
  color:var(--muted);
  background:color-mix(in srgb,#f59e0b 6%,var(--surface));
}

/* table */
.d-table{width:100%;border-collapse:collapse;font-size:13px;margin-bottom:14px}
.d-table th{text-align:left;font-size:11px;font-weight:600;letter-spacing:0.05em;text-transform:uppercase;color:var(--faint);padding:6px 10px;border-bottom:1px solid var(--border)}
.d-table td{padding:8px 10px;border-bottom:1px solid var(--border-soft);color:var(--muted);vertical-align:top}
.d-table td:first-child{color:var(--ink);font-family:var(--mono);font-size:12px;white-space:nowrap}
.d-table tr:last-child td{border-bottom:none}

/* code blocks */
.d-code{border-radius:8px;overflow:hidden;border:1px solid var(--border);margin-bottom:14px;background:var(--code-bg)}
.d-code-bar{
  display:flex;align-items:center;justify-content:space-between;
  padding:8px 12px;
  border-bottom:1px solid color-mix(in srgb,white 8%,transparent);
  font-family:var(--mono);font-size:11px;color:var(--code-comment);
}
.d-copy{
  border:1px solid color-mix(in srgb,white 15%,transparent);
  background:transparent;
  color:var(--code-comment);
  border-radius:5px;
  padding:3px 8px;
  font-family:var(--mono);font-size:11px;
  cursor:pointer;transition:color 0.15s;
}
.d-copy:hover{color:var(--code-fg)}
.d-code pre{
  padding:14px;
  font-family:var(--mono);font-size:12.5px;
  line-height:1.75;
  color:var(--code-fg);
  overflow:auto;
}

/* inline code */
.d-section code{
  font-family:var(--mono);font-size:12px;
  background:var(--border-soft);
  color:var(--ink);
  padding:1px 5px;border-radius:4px;
}

@media(max-width:720px){
  .d-wrap{flex-direction:column;gap:24px}
  .d-sidebar{width:100%}
  .d-sidebar-sticky{position:static}
}
`

function CodeBlock({ title, code, id }: { title: string; code: string; id: string }) {
  const [copied, setCopied] = useState(false)
  async function onCopy() {
    try { await navigator.clipboard.writeText(code); setCopied(true); setTimeout(() => setCopied(false), 1500) } catch { /* */ }
  }
  return (
    <div className="d-code">
      <div className="d-code-bar">
        <span>{title}</span>
        <button type="button" className="d-copy" onClick={onCopy} aria-label={`Copy ${id}`}>{copied ? 'Copied' : 'Copy'}</button>
      </div>
      <pre>{code}</pre>
    </div>
  )
}

type Section = 'dns-setup' | 'migrate' | 'migrate-selfhosted' | 'webhook-quickstart' | 'webhook-ref' | 'filestore-recovery'

const HEADING_TO_SECTION: Record<string, Section> = {
  'dns-setup': 'dns-setup',
  'dns-before': 'dns-setup',
  'dns-records': 'dns-setup',
  'dns-providers': 'dns-setup',
  'dns-propagation': 'dns-setup',
  'migrate': 'migrate',
  'migrate-before': 'migrate',
  'migrate-1': 'migrate',
  'migrate-2': 'migrate',
  'migrate-3': 'migrate',
  'migrate-4': 'migrate',
  'migrate-5': 'migrate',
  'migrate-6': 'migrate',
  'migrate-7': 'migrate',
  'migrate-troubleshooting': 'migrate',
  'migrate-selfhosted': 'migrate-selfhosted',
  'sh-before': 'migrate-selfhosted',
  'sh-1': 'migrate-selfhosted',
  'sh-2': 'migrate-selfhosted',
  'sh-3': 'migrate-selfhosted',
  'sh-4': 'migrate-selfhosted',
  'sh-5': 'migrate-selfhosted',
  'sh-6': 'migrate-selfhosted',
  'sh-7': 'migrate-selfhosted',
  'sh-8': 'migrate-selfhosted',
  'sh-troubleshooting': 'migrate-selfhosted',
  'webhook-quickstart': 'webhook-quickstart',
  'wq-before': 'webhook-quickstart',
  'wq-1': 'webhook-quickstart',
  'wq-2': 'webhook-quickstart',
  'wq-3': 'webhook-quickstart',
  'wq-not-trigger': 'webhook-quickstart',
  'webhook-ref': 'webhook-ref',
  'wr-how': 'webhook-ref',
  'filestore-recovery': 'filestore-recovery',
  'fs-when': 'filestore-recovery',
  'fs-export': 'filestore-recovery',
  'fs-verify': 'filestore-recovery',
  'fs-upload': 'filestore-recovery',
  'fs-troubleshooting': 'filestore-recovery',
  'wr-step1': 'webhook-ref',
  'wr-step2': 'webhook-ref',
  'wr-testing': 'webhook-ref',
  'wr-responses': 'webhook-ref',
  'wr-troubleshooting': 'webhook-ref',
}

const COPY_FEEDBACK_MS = 1500

function SectionHeading({ id, children }: { id: string; children: React.ReactNode }) {
  const [copied, setCopied] = useState(false)
  function onCopy() {
    const url = `${window.location.origin}${window.location.pathname}#${id}`
    navigator.clipboard.writeText(url).then(
      () => { setCopied(true); setTimeout(() => setCopied(false), COPY_FEEDBACK_MS) },
      () => { /* clipboard not available */ }
    )
  }
  return (
    <h2 id={id}>
      <span>{children}</span>
      <button type="button" className="d-h-anchor" onClick={onCopy} title="Copy link" aria-label="Copy link to section">
        {copied ? '✓' : '#'}
      </button>
    </h2>
  )
}

// Custom-domain DNS guide. Its readers are mostly Turkish customers, so unlike
// the rest of this page it follows the UI language. The A-record IP comes from
// the build (VITE_SERVER_IP) and the CNAME target is the host serving this
// page, so every deployment shows its own values. Markdown copy of the same
// guide: docs/custom-domain-dns.md.
function DnsGuide({ lang, copyButton }: { lang: 'tr' | 'en'; copyButton: React.ReactNode }) {
  const host = window.location.hostname
  const ipText = branding.serverIp || (lang === 'tr' ? '<sunucu IP adresi>' : '<server IP address>')
  const ip = <code>{ipText}</code>
  const cname = <code>{host}</code>

  if (lang === 'tr') {
    return (
      <>
        <div className="d-page-header">
          <h1>Alan adı DNS kurulumu</h1>
          <p>Odoo kurulumunuz hazır ve çalışıyor. Alan adınızı yayına almak için geriye tek bir adım kaldı: DNS kaydınızı sunucumuza yönlendirmek. İşlem alan adı sağlayıcınızın panelinden birkaç dakika sürer.</p>
          {copyButton}
        </div>

        <div className="d-section">
          <SectionHeading id="dns-before">Başlamadan önce</SectionHeading>
          <ul>
            <li>Alan adını panelde kurulumunuzun <strong>Alan Adları</strong> sekmesine ekleyin. SSL sertifikası bu kayıt ve DNS yönlendirmesi tamamlanınca otomatik alınır.</li>
            <li>Alan adı sağlayıcınızın (Cloudflare, GoDaddy, Route 53 vb.) DNS paneline erişiminiz olmalı.</li>
          </ul>
        </div>

        <div className="d-section">
          <SectionHeading id="dns-records">Eklemeniz gereken kayıtlar</SectionHeading>
          <p>Önerilen yöntem <strong>A kaydı</strong>: alan adınızı doğrudan {ip} adresine bağlar. Panelinizdeki "DNS Yönetimi" bölümüne gidip aşağıdaki kayıtları ekleyin.</p>
          <table className="d-table">
            <thead><tr><th>Tür</th><th>Ad / Host</th><th>Değer / Hedef</th><th>TTL</th></tr></thead>
            <tbody>
              <tr><td>A</td><td><code>@</code> (kök alan adı)</td><td>{ip}</td><td>Auto / 3600</td></tr>
              <tr><td>A</td><td><code>www</code></td><td>{ip}</td><td>Auto / 3600</td></tr>
            </tbody>
          </table>
          <div className="d-note"><strong>Alt alan adı mı kullanıyorsunuz?</strong> (ör. <code>odoo.sirketiniz.com</code>) A kaydı yerine bir CNAME kaydı oluşturup hedefi {cname} yapabilirsiniz. Kök alan adında A kaydını tercih edin.</div>
          {!branding.serverIp && (
            <div className="d-note">Sunucu IP adresini platform yöneticinizden alın ya da <code>dig +short {host}</code> komutuyla öğrenin.</div>
          )}
        </div>

        <div className="d-section">
          <SectionHeading id="dns-providers">Sağlayıcıya göre adım adım</SectionHeading>
          <p>Alan adınızı nereden aldıysanız o başlığı takip edin.</p>
          <h3>Cloudflare</h3>
          <ol>
            <li>Cloudflare hesabınıza girin ve ilgili alan adını seçin.</li>
            <li>Sol menüden <strong>DNS → Records</strong> bölümüne gidin.</li>
            <li><strong>Add record</strong> butonuna tıklayın.</li>
            <li>Type: <code>A</code> · Name: <code>@</code> · IPv4 address: {ip}</li>
            <li>Proxy status'ü <strong>DNS only (gri bulut)</strong> yapın.</li>
            <li><code>www</code> için de aynı kaydı tekrarlayıp <strong>Save</strong> deyin.</li>
          </ol>
          <div className="d-warn">Turuncu bulut (proxy) kapalı olmalı. Açık kalırsa SSL sertifikası alınamaz ve trafik sunucuya ulaşmaz.</div>
          <h3>GoDaddy</h3>
          <ol>
            <li>GoDaddy hesabınızda <strong>My Products</strong> → alan adınızın yanındaki <strong>DNS</strong>'e tıklayın.</li>
            <li><strong>Records</strong> bölümünde <strong>Add</strong> butonuna basın.</li>
            <li>Type: <code>A</code> · Name: <code>@</code> · Value: {ip} · TTL: 1 Hour</li>
            <li><strong>Save</strong> deyin. <code>www</code> için Name kısmına <code>www</code> yazıp işlemi tekrarlayın.</li>
            <li>Varsayılan "Parked" A kaydı varsa onu düzenleyip IP'yi değiştirin.</li>
          </ol>
          <h3>Amazon Route 53</h3>
          <ol>
            <li><strong>AWS Console → Route 53 → Hosted zones</strong> → alan adınızı seçin.</li>
            <li><strong>Create record</strong> butonuna tıklayın.</li>
            <li>Record name: boş (kök) · Record type: <code>A</code> · Value: {ip}</li>
            <li>TTL: <code>300</code> · Routing policy: Simple routing</li>
            <li><strong>Create records</strong> deyin. <code>www</code> için ayrı bir kayıt daha oluşturun.</li>
          </ol>
          <h3>Diğer sağlayıcılar</h3>
          <ol>
            <li>Sağlayıcınızın <strong>DNS Yönetimi / DNS Zone</strong> panelini açın.</li>
            <li>Yeni bir A kaydı ekleyin: Host <code>@</code>, Değer {ip}.</li>
            <li><code>www</code> için ikinci bir A kaydı ekleyin.</li>
            <li>Kaydedin. "Nameserver değiştir" seçeneğine dokunmayın, sadece A kaydı yeterlidir.</li>
          </ol>
        </div>

        <div className="d-section">
          <SectionHeading id="dns-propagation">Yayına geçiş süresi</SectionHeading>
          <p>DNS değişiklikleri genellikle 5 ila 30 dakika içinde aktif olur, nadiren 24 saati bulabilir. SSL sertifikanız alan adınız yönlendikten sonra otomatik oluşturulur, sizin bir şey yapmanıza gerek yok.</p>
          <CodeBlock title="Yönlendirmeyi kontrol edin" code={`dig +short siteniz.com\n# Çıktı ${ipText} olmalı`} id="dns-dig" />
          <p>Kaydınızı ekledikten sonra takıldığınız yerde bize yazın, yardımcı olalım.</p>
        </div>
      </>
    )
  }

  return (
    <>
      <div className="d-page-header">
        <h1>Custom domain DNS setup</h1>
        <p>Your Odoo instance is up and running. One step is left to put it on your own domain: point your DNS at our server. It takes a few minutes in your domain registrar's panel.</p>
        {copyButton}
      </div>

      <div className="d-section">
        <SectionHeading id="dns-before">Before you start</SectionHeading>
        <ul>
          <li>Add the domain in your instance's <strong>Domains</strong> tab. The TLS certificate is issued automatically once that entry exists and DNS points here.</li>
          <li>Access to your DNS provider's panel (Cloudflare, GoDaddy, Route 53, etc.).</li>
        </ul>
      </div>

      <div className="d-section">
        <SectionHeading id="dns-records">Records to add</SectionHeading>
        <p>The recommended method is an <strong>A record</strong>: it points your domain straight at {ip}. Open the DNS management section of your provider and add these records.</p>
        <table className="d-table">
          <thead><tr><th>Type</th><th>Name / Host</th><th>Value / Target</th><th>TTL</th></tr></thead>
          <tbody>
            <tr><td>A</td><td><code>@</code> (root domain)</td><td>{ip}</td><td>Auto / 3600</td></tr>
            <tr><td>A</td><td><code>www</code></td><td>{ip}</td><td>Auto / 3600</td></tr>
          </tbody>
        </table>
        <div className="d-note"><strong>Using a subdomain?</strong> (e.g. <code>odoo.yourcompany.com</code>) You can create a CNAME record pointing at {cname} instead of an A record. On the root domain, use an A record.</div>
        {!branding.serverIp && (
          <div className="d-note">Get the server IP from your platform administrator, or look it up with <code>dig +short {host}</code>.</div>
        )}
      </div>

      <div className="d-section">
        <SectionHeading id="dns-providers">Step by step, per provider</SectionHeading>
        <p>Follow the heading for wherever your domain is registered.</p>
        <h3>Cloudflare</h3>
        <ol>
          <li>Sign in to Cloudflare and select the domain.</li>
          <li>Go to <strong>DNS → Records</strong> in the left menu.</li>
          <li>Click <strong>Add record</strong>.</li>
          <li>Type: <code>A</code> · Name: <code>@</code> · IPv4 address: {ip}</li>
          <li>Set Proxy status to <strong>DNS only (grey cloud)</strong>.</li>
          <li>Repeat for <code>www</code> and click <strong>Save</strong>.</li>
        </ol>
        <div className="d-warn">The orange cloud (proxy) must be off. With it on, the TLS certificate cannot be issued and traffic never reaches the server.</div>
        <h3>GoDaddy</h3>
        <ol>
          <li>In your GoDaddy account, open <strong>My Products</strong> and click <strong>DNS</strong> next to your domain.</li>
          <li>Click <strong>Add</strong> in the <strong>Records</strong> section.</li>
          <li>Type: <code>A</code> · Name: <code>@</code> · Value: {ip} · TTL: 1 Hour</li>
          <li>Click <strong>Save</strong>. Repeat with <code>www</code> as the Name.</li>
          <li>If there is a default "Parked" A record, edit it and change the IP.</li>
        </ol>
        <h3>Amazon Route 53</h3>
        <ol>
          <li><strong>AWS Console → Route 53 → Hosted zones</strong>, then select your domain.</li>
          <li>Click <strong>Create record</strong>.</li>
          <li>Record name: empty (root) · Record type: <code>A</code> · Value: {ip}</li>
          <li>TTL: <code>300</code> · Routing policy: Simple routing</li>
          <li>Click <strong>Create records</strong>. Create a separate record for <code>www</code>.</li>
        </ol>
        <h3>Other providers</h3>
        <ol>
          <li>Open your provider's <strong>DNS management / DNS zone</strong> panel.</li>
          <li>Add an A record: Host <code>@</code>, Value {ip}.</li>
          <li>Add a second A record for <code>www</code>.</li>
          <li>Save. Leave the "change nameservers" option alone; the A record is all you need.</li>
        </ol>
      </div>

      <div className="d-section">
        <SectionHeading id="dns-propagation">Propagation</SectionHeading>
        <p>DNS changes usually take effect within 5 to 30 minutes, and rarely up to 24 hours. Your TLS certificate is issued automatically once the domain points here; there is nothing for you to do.</p>
        <CodeBlock title="Check the record" code={`dig +short yourdomain.com\n# Should print ${ipText}`} id="dns-dig" />
        <p>If you get stuck after adding the record, write to us and we will help.</p>
      </div>
    </>
  )
}

export default function DocsPage() {
  const [active, setActive] = useState<Section>('migrate')
  const { i18n } = useTranslation()
  const dnsLang = i18n.language?.startsWith('tr') ? 'tr' : 'en'
  const [pageCopied, setPageCopied] = useState(false)
  const navigate = useNavigate()
  const location = useLocation()
  const contentRef = useRef<HTMLElement>(null)
  const webhookUrl = `${window.location.origin}/api/v1/webhooks/github`

  // Resolve section from URL hash on load and on hash change
  useEffect(() => {
    const hash = location.hash.slice(1)
    if (!hash) return
    const section = HEADING_TO_SECTION[hash]
    if (section) {
      setActive(section)
      if (hash !== section) {
        // Scroll to specific heading after section renders — two rAF ticks ensure
        // the conditional section has been painted before we measure element position.
        requestAnimationFrame(() => {
          requestAnimationFrame(() => {
            document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
          })
        })
      } else {
        window.scrollTo({ top: 0 })
      }
    }
  }, [location.hash])

  function goTo(section: Section) {
    setActive(section)
    navigate(`/docs#${section}`, { replace: true })
    window.scrollTo({ top: 0 })
  }

  function onCopyPage() {
    const text = contentRef.current?.innerText ?? ''
    navigator.clipboard.writeText(text).then(
      () => { setPageCopied(true); setTimeout(() => setPageCopied(false), COPY_FEEDBACK_MS) },
      () => { /* clipboard not available */ }
    )
  }

  const actionYaml = `name: Trigger Addon Sync

on:
  push:
    branches: [main]
  release:
    types: [published]

jobs:
  notify:
    runs-on: ubuntu-latest
    steps:
      - name: Sign and send
        env:
          OAAS_WEBHOOK_URL: \${{ secrets.OAAS_WEBHOOK_URL }}
          OAAS_WEBHOOK_SECRET: \${{ secrets.OAAS_WEBHOOK_SECRET }}
          EVENT_NAME: \${{ github.event_name }}
        run: |
          set -euo pipefail
          body='{"repository":{"html_url":"\${{ github.server_url }}/\${{ github.repository }}"},"ref":"\${{ github.ref }}","after":"\${{ github.sha }}"}'
          sig="$(printf '%s' "$body" | openssl dgst -sha256 -hmac "$OAAS_WEBHOOK_SECRET" -hex | sed 's/^.* //')"
          event="push"; [ "$EVENT_NAME" = "release" ] && event="release"
          curl -sS -X POST "$OAAS_WEBHOOK_URL" \\
            -H "Content-Type: application/json" \\
            -H "X-GitHub-Event: $event" \\
            -H "X-Hub-Signature-256: sha256=$sig" \\
            --data "$body"`

  const curlTest = `BASE_URL="${window.location.origin}"
SECRET="<WEBHOOK_SECRET>"
BODY='{"repository":{"html_url":"https://github.com/<owner>/<repo>"}}'
SIG="$(printf '%s' "$BODY" | openssl dgst -sha256 -hmac "$SECRET" -hex | sed 's/^.* //')"

curl -i -X POST "$BASE_URL/api/v1/webhooks/github" \\
  -H "Content-Type: application/json" \\
  -H "X-GitHub-Event: push" \\
  -H "X-Hub-Signature-256: sha256=$SIG" \\
  --data "$BODY"`

  const dbSetup = `docker compose exec -T platform-db psql -U platform -d platform <<'SQL'
-- Replace all placeholder values before running.
WITH upsert_source AS (
  INSERT INTO addon_sources (repo_url, ref, webhook_secret)
  VALUES (
    'https://github.com/<owner>/<repo>',
    'main',
    '<WEBHOOK_SECRET>'
  )
  ON CONFLICT DO NOTHING
  RETURNING id
), source_row AS (
  SELECT id FROM upsert_source
  UNION ALL
  SELECT id FROM addon_sources
  WHERE repo_url = 'https://github.com/<owner>/<repo>' LIMIT 1
)
UPDATE addons
SET source_id = (SELECT id FROM source_row)
WHERE id = '<ADDON_ID>'::uuid;
SQL`

  const pgDumpHost = `pg_dump -h localhost -p 5432 -U odoo -Fc my_odoo_db > my_odoo_db.dump`
  const pgDumpContainer = `docker exec <db-container> pg_dump -U odoo -Fc my_odoo_db > my_odoo_db.dump`

  const fsDumpVolume = `docker run --rm \\
  -v <odoo-volume>:/odoo_data \\
  -v "$(pwd)":/out \\
  alpine:3 \\
  tar czf /out/filestore.tar.gz -C /odoo_data/filestore/<subdomain> .`
  const fsDumpDirectory = `tar czf filestore.tar.gz -C /path/to/filestore/<subdomain> .`
  const fsVerify = `tar tzf filestore.tar.gz | head -5
# Expected output (paths start with ./):
# ./
# ./ab/
# ./ab/ab1234…
# ./cd/
# ./cd/cd5678…`
  const fsListVolumes = `docker volume ls | grep <subdomain>`

  // --- self-hosted (systemd / .deb) migration snippets ---
  const shInventory = `# Read the running config - note addons_path, data_dir, and the db_* values.
sudo grep -E 'addons_path|data_dir|db_host|db_user|db_password|dbfilter' /etc/odoo/odoo.conf

# List databases (ignore postgres / template0 / template1).
PGPASSWORD=<db_password> psql -h localhost -U <db_user> -l`
  const shEdition = `sudo /usr/bin/odoo --version
# "Odoo Server 19.0+e-…"  -> Enterprise  (the +e matters - see the warning below)
# "Odoo Server 19.0"      -> Community`
  const shDump = `# Custom format: smaller than plain SQL and restored natively by the platform.
PGPASSWORD=<db_password> pg_dump -h localhost -U <db_user> -Fc -d <db> -f <db>.dump`
  const shFilestore = `# .deb / systemd installs keep the filestore under the odoo user's home when
# data_dir is unset (the default is ~/.local/share/Odoo). Archive its CONTENTS
# so the paths start with ./ - that is the layout the platform restore expects.
sudo tar czf filestore.tar.gz \\
  -C /var/lib/odoo/.local/share/Odoo/filestore/<db> .`
  const shListAddons = `# Each entry in addons_path may hold several custom modules.
sudo ls -1 /var/likit-odoo/addons`
  const shNeutralize = `# Freeze background work on the OLD server during the cutover window so it does
# not keep firing crons, sending mail, or calling external systems behind your back.
sudo systemctl stop odoo`

  return (
    <div className="docs">
      <style>{DOCS_CSS}</style>

      <nav className="d-nav">
        <div className="d-nav-inner">
          <Link to="/" className="d-nav-brand">Platform Docs</Link>
          <div className="d-nav-links">
            <Link to="/login">Sign in</Link>
            <a href="https://github.com" target="_blank" rel="noreferrer">GitHub</a>
          </div>
        </div>
      </nav>

      <div className="d-wrap">
        <aside className="d-sidebar">
          <div className="d-sidebar-sticky">
            <div className="d-nav-group">
              <div className="d-nav-label">{dnsLang === 'tr' ? 'Alan adları' : 'Domains'}</div>
              <span className={`d-nav-item${active === 'dns-setup' ? ' active' : ''}`} onClick={() => goTo('dns-setup')}>
                {dnsLang === 'tr' ? 'Alan adı DNS kurulumu' : 'Custom domain DNS setup'}
              </span>
            </div>
            <div className="d-nav-group">
              <div className="d-nav-label">Migrations</div>
              <span className={`d-nav-item${active === 'migrate' ? ' active' : ''}`} onClick={() => goTo('migrate')}>
                Docker Compose to platform
              </span>
              <span className={`d-nav-item${active === 'migrate-selfhosted' ? ' active' : ''}`} onClick={() => goTo('migrate-selfhosted')}>
                Self-hosted (systemd / .deb)
              </span>
            </div>
            <div className="d-nav-group">
              <div className="d-nav-label">Backups</div>
              <span className={`d-nav-item${active === 'filestore-recovery' ? ' active' : ''}`} onClick={() => goTo('filestore-recovery')}>
                Filestore recovery
              </span>
            </div>
            <div className="d-nav-group">
              <div className="d-nav-label">Addon Automation</div>
              <span className={`d-nav-item${active === 'webhook-quickstart' ? ' active' : ''}`} onClick={() => goTo('webhook-quickstart')}>
                GitHub Webhook - Quickstart
              </span>
              <span className={`d-nav-item${active === 'webhook-ref' ? ' active' : ''}`} onClick={() => goTo('webhook-ref')}>
                GitHub Webhook - Reference
              </span>
            </div>
          </div>
        </aside>

        <main className="d-content" ref={contentRef}>

          {active === 'dns-setup' && (
            <DnsGuide
              lang={dnsLang}
              copyButton={<button type="button" className="d-copy-page" onClick={onCopyPage}>{pageCopied ? '✓ Copied' : '⎘ Copy for AI'}</button>}
            />
          )}

          {active === 'migrate' && (
            <>
              <div className="d-page-header">
                <h1>Migrate a self-managed instance</h1>
                <p>Move an Odoo instance running in Docker Compose to the platform. Data, modules, and configuration carry over intact.</p>
                <button type="button" className="d-copy-page" onClick={onCopyPage}>{pageCopied ? '✓ Copied' : '⎘ Copy for AI'}</button>
              </div>

              <div className="d-section">
                <SectionHeading id="migrate-before">Before you start</SectionHeading>
                <ul>
                  <li>SSH access to the host machine running the instance</li>
                  <li><code>pg_dump</code> available on that host or inside the database container</li>
                  <li>A platform account on a <strong>dev</strong> or <strong>pro</strong> plan</li>
                  <li>The Odoo major version of your instance (16, 17, 18, or 19)</li>
                </ul>
              </div>

              <div className="d-section">
                <SectionHeading id="migrate-1">1. Find the database name</SectionHeading>
                <p>Check your compose file for the database connection your Odoo container uses. The database name is whatever Odoo was initialized with.</p>
                <p>If you are unsure, list databases directly:</p>
                <CodeBlock title="PostgreSQL on the host" code="psql -h localhost -p 5432 -U odoo -l" id="list-db-host" />
                <CodeBlock title="PostgreSQL in a container" code="docker exec <db-container> psql -U odoo -l" id="list-db-container" />
                <p>Ignore <code>postgres</code>, <code>template0</code>, and <code>template1</code>.</p>
              </div>

              <div className="d-section">
                <SectionHeading id="migrate-2">2. Export the database</SectionHeading>
                <p>Dump in custom format - this is what the platform restore uses natively and produces a smaller file than plain SQL.</p>
                <CodeBlock title="PostgreSQL on the host" code={pgDumpHost} id="dump-host" />
                <CodeBlock title="PostgreSQL in a container" code={pgDumpContainer} id="dump-container" />
                <div className="d-note">Upload limit is 500 MB. For larger databases, trim data before exporting.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="migrate-3">3. Create a new instance</SectionHeading>
                <ol>
                  <li>Click <strong>New Instance</strong> in the dashboard.</li>
                  <li>Pick a subdomain - you can update the domain after migration.</li>
                  <li>Select the <strong>exact same Odoo major version</strong> as your source instance.</li>
                  <li>Wait for the instance to reach <strong>Running</strong> status.</li>
                </ol>
                <div className="d-warn">Version mismatches (e.g. restoring a v17 dump into a v18 instance) will break the instance.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="migrate-4">4. Upload the dump</SectionHeading>
                <ol>
                  <li>Open the instance, go to the <strong>Backups</strong> tab.</li>
                  <li>Click <strong>Upload Backup</strong> and select <code>my_odoo_db.dump</code>.</li>
                  <li>Wait for the upload - the file appears in the list with source <code>upload</code>.</li>
                </ol>
              </div>

              <div className="d-section">
                <SectionHeading id="migrate-5">5. Restore</SectionHeading>
                <ol>
                  <li>Find the uploaded backup in the list.</li>
                  <li>Click <strong>Restore</strong> and confirm.</li>
                  <li>Status changes to <strong>Restoring</strong>, then back to <strong>Running</strong> when done.</li>
                </ol>
                <p>Log in with your existing credentials - data, installed modules, and settings carry over.</p>
                <div className="d-note">Restore replaces all data in the instance. On a freshly created instance there is nothing to lose.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="migrate-6">6. Re-upload custom addons</SectionHeading>
                <p>The dump carries over module records, but the source files live on the old host. For each custom addon, open the <strong>Addons</strong> tab and install from ZIP or GitHub. Standard Odoo apps are already available and need no action.</p>
              </div>

              <div className="d-section">
                <SectionHeading id="migrate-7">7. Switch your domain (optional)</SectionHeading>
                <p>Open the <strong>Domains</strong> tab, add your domain, and update your DNS record to point at the platform server (see <span className="d-nav-item" style={{ display: 'inline', padding: 0, color: 'var(--accent)' }} onClick={() => goTo('dns-setup')}>Custom domain DNS setup</span>). TLS is handled automatically.</p>
              </div>

              <div className="d-section">
                <SectionHeading id="migrate-troubleshooting">Troubleshooting</SectionHeading>
                <table className="d-table">
                  <thead><tr><th>Problem</th><th>Fix</th></tr></thead>
                  <tbody>
                    <tr><td>Restore fails immediately</td><td>Instance must be <strong>Running</strong> before restore can start.</td></tr>
                    <tr><td>Modules error after restore</td><td>Custom addon files are missing. Re-upload via the Addons tab.</td></tr>
                    <tr><td>Stuck in Restoring</td><td>Check Logs tab. Common cause: PostgreSQL major version mismatch. Re-export with <code>--no-acl --no-owner</code>.</td></tr>
                    <tr><td>Upload rejected</td><td>File must end in <code>.dump</code> or <code>.sql</code>. Max 500 MB.</td></tr>
                  </tbody>
                </table>
              </div>
            </>
          )}

          {active === 'migrate-selfhosted' && (
            <>
              <div className="d-page-header">
                <h1>Migrate a self-hosted Odoo (systemd / .deb)</h1>
                <p>Move an Odoo installed straight onto a server - a <code>.deb</code> package run under systemd, or a bare-metal source build - onto the platform. Unlike a Docker Compose source there is no volume to copy: the filestore lives in the <code>odoo</code> user's home and the service is managed by <code>systemctl</code>.</p>
                <button type="button" className="d-copy-page" onClick={onCopyPage}>{pageCopied ? '✓ Copied' : '⎘ Copy for AI'}</button>
              </div>

              <div className="d-section">
                <SectionHeading id="sh-before">Before you start</SectionHeading>
                <ul>
                  <li>SSH access to the source server, with <code>sudo</code> (the config and filestore are owned by <code>root</code>/<code>odoo</code>).</li>
                  <li>The database password - read it from <code>db_password</code> in <code>odoo.conf</code> (step 1).</li>
                  <li>A platform account on a <strong>dev</strong> or <strong>pro</strong> plan.</li>
                  <li>The Odoo major version <em>and edition</em> of your source (step 2 - this is the most common migration blocker).</li>
                </ul>
                <div className="d-note">Two passwords are easy to confuse. <code>admin_passwd</code> in <code>odoo.conf</code> is the <strong>master password</strong> for the database manager - it is <em>not</em> the web login of the <code>admin</code> user. The login password lives hashed in <code>res_users</code> and carries over with the dump.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="sh-1">1. Inventory the install</SectionHeading>
                <p>Everything you need is in the running config. Note the <code>addons_path</code> (where custom modules live), <code>data_dir</code> (where the filestore lives - defaults to the odoo home when unset), and the database name.</p>
                <CodeBlock title="Read config + list databases" code={shInventory} id="sh-inventory" />
              </div>

              <div className="d-section">
                <SectionHeading id="sh-2">2. Check the edition - Community vs Enterprise</SectionHeading>
                <CodeBlock title="Edition check" code={shEdition} id="sh-edition" />
                <div className="d-warn"><strong>Enterprise sources need extra work.</strong> The platform provisions the <strong>Community</strong> Odoo image. If your database has Enterprise apps installed (e.g. <code>account_reports</code>, <code>account_accountant</code>, <code>documents</code>, <code>web_studio</code>), a plain restore will fail to load those modules. Either supply the Enterprise addons to the instance (ask your platform admin to mount them) before restoring, or uninstall the Enterprise apps on the source first. Restoring an Enterprise DB onto a Community instance without the addons leaves it stuck on boot.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="sh-3">3. Take a full backup - database AND filestore</SectionHeading>
                <p>A database dump alone is <strong>not</strong> a complete migration: attachments, product images, and report assets live in the filestore on disk. Export both.</p>
                <CodeBlock title="3a. Database (custom format)" code={shDump} id="sh-dump" />
                <CodeBlock title="3b. Filestore" code={shFilestore} id="sh-filestore" />
                <div className="d-note">Database upload limit is 500 MB. The filestore goes through the Filestore recovery flow (step 6) and has no such limit. For a very large DB, trim history before dumping.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="sh-4">4. Create a matching instance</SectionHeading>
                <ol>
                  <li>Click <strong>New Instance</strong> and pick a subdomain (you can attach your real domain later).</li>
                  <li>Select the <strong>exact same Odoo major version</strong> as the source.</li>
                  <li>Wait for <strong>Running</strong> status.</li>
                </ol>
                <div className="d-warn">Restoring across major versions (e.g. a v17 dump into a v18 instance) breaks the instance. Match the version exactly.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="sh-5">5. Restore the database</SectionHeading>
                <ol>
                  <li>Open the instance → <strong>Backups</strong> tab → <strong>Upload Backup</strong>, select <code>&lt;db&gt;.dump</code>.</li>
                  <li>Find it in the list (source <code>upload</code>) and click <strong>Restore</strong>.</li>
                  <li>Status moves to <strong>Restoring</strong>, then back to <strong>Running</strong>.</li>
                </ol>
                <div className="d-note">Restore replaces all data in the instance. On a freshly created instance there is nothing to lose.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="sh-6">6. Restore the filestore</SectionHeading>
                <p>Upload the <code>filestore.tar.gz</code> from step 3b using the <span className="d-nav-item" style={{ display: 'inline', padding: 0, color: 'var(--accent)' }} onClick={() => goTo('filestore-recovery')}>Filestore recovery</span> flow. Without this, records load but their attachments and images render as broken links.</p>
              </div>

              <div className="d-section">
                <SectionHeading id="sh-7">7. Re-install custom addons</SectionHeading>
                <p>The dump carries the module <em>records</em>, but the Python/XML source files lived in <code>addons_path</code> on the old host. List them, then re-add each from the <strong>Addons</strong> tab (upload ZIP or install from GitHub).</p>
                <CodeBlock title="List custom modules on the source" code={shListAddons} id="sh-list-addons" />
                <ul>
                  <li>Install in <strong>dependency order</strong> - a module's <code>depends</code> must already be present.</li>
                  <li>ZIPs authored on Windows (paths with <code>{'\\'}</code> separators) are handled automatically; no need to repackage them.</li>
                  <li>Standard Odoo apps are already in the image and need no action.</li>
                </ul>
              </div>

              <div className="d-section">
                <SectionHeading id="sh-8">8. Validate, then cut over</SectionHeading>
                <p>Test the platform copy thoroughly <strong>before</strong> pointing your domain at it - log in, open key records, check images, run a report. While you test, the source is still live; nothing has moved yet.</p>
                <p>When you are satisfied, freeze the source so it cannot keep firing scheduled jobs, sending mail, or syncing to external systems behind your back, then switch DNS:</p>
                <CodeBlock title="Freeze the old server at cutover" code={shNeutralize} id="sh-neutralize" />
                <ol>
                  <li>Stop the source service (above) so crons and outbound mail stop firing from the old box.</li>
                  <li>On the platform, open the <strong>Domains</strong> tab, add your domain, and update DNS to point at the platform. TLS is issued automatically.</li>
                  <li>Confirm the live site responds on your domain, then decommission the old server.</li>
                </ol>
                <div className="d-warn">If you skip the freeze, both the old and new copy run their crons in parallel - duplicated outbound mail, double API calls to external systems (banks, e-invoice, ERP connectors), and divergent data. Always stop the source at cutover.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="sh-troubleshooting">Troubleshooting</SectionHeading>
                <table className="d-table">
                  <thead><tr><th>Problem</th><th>Fix</th></tr></thead>
                  <tbody>
                    <tr><td>Instance stuck after restore</td><td>Usually Enterprise modules with no source files. Supply the Enterprise addons or uninstall those apps on the source (step 2).</td></tr>
                    <tr><td>Attachments / images broken</td><td>Filestore was not restored. Complete step 6.</td></tr>
                    <tr><td>Custom module fails to install</td><td>A dependency is missing. Install <code>depends</code> modules first (step 7).</td></tr>
                    <tr><td>Can't find the filestore</td><td><code>data_dir</code> is unset, so it is under the odoo home: <code>/var/lib/odoo/.local/share/Odoo/filestore/&lt;db&gt;</code>.</td></tr>
                    <tr><td>Can't log in as admin after restore</td><td>The web login is the <code>res_users</code> password, not <code>admin_passwd</code>. It carries over from the dump; if lost, an admin can reset it.</td></tr>
                    <tr><td>Stuck in Restoring</td><td>Check the Logs tab. Common cause: PostgreSQL major version mismatch. Re-export with <code>--no-acl --no-owner</code>.</td></tr>
                  </tbody>
                </table>
              </div>
            </>
          )}

          {active === 'webhook-quickstart' && (
            <>
              <div className="d-page-header">
                <h1>GitHub Webhook - Quickstart</h1>
                <p>Auto-update an addon on every push. Start here if your platform admin has already linked the addon source.</p>
                <button type="button" className="d-copy-page" onClick={onCopyPage}>{pageCopied ? '✓ Copied' : '⎘ Copy for AI'}</button>
              </div>

              <div className="d-section">
                <SectionHeading id="wq-before">Before you start</SectionHeading>
                <ul>
                  <li>Addon is installed from GitHub (source URL is set).</li>
                  <li>Instance is running.</li>
                  <li>You have admin access to the GitHub repository.</li>
                </ul>
              </div>

              <div className="d-section">
                <SectionHeading id="wq-1">1. Install the addon from GitHub</SectionHeading>
                <ol>
                  <li>Open the instance, go to <strong>Addons</strong>.</li>
                  <li>Choose <strong>Install from GitHub</strong>.</li>
                  <li>Enter the repository URL and ref (<code>main</code> or your branch).</li>
                  <li>Click <strong>Install</strong>.</li>
                </ol>
                <p>For private repositories, enable the Private toggle, copy the deploy key, and add it to the GitHub repository as a read-only deploy key.</p>
              </div>

              <div className="d-section">
                <SectionHeading id="wq-2">2. Add a webhook in GitHub</SectionHeading>
                <p>In the repository, go to <strong>Settings → Webhooks → Add webhook</strong>:</p>
                <table className="d-table">
                  <thead><tr><th>Field</th><th>Value</th></tr></thead>
                  <tbody>
                    <tr><td>Payload URL</td><td>{webhookUrl}</td></tr>
                    <tr><td>Content type</td><td>application/json</td></tr>
                    <tr><td>Secret</td><td>Provided by your platform admin</td></tr>
                    <tr><td>Events</td><td>Just the push event</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="d-section">
                <SectionHeading id="wq-3">3. Verify with a push</SectionHeading>
                <ol>
                  <li>Commit and push a change to the configured branch.</li>
                  <li>Open the <strong>Addons</strong> tab in the platform.</li>
                  <li>Open the addon's job log and confirm an update job completed.</li>
                </ol>
              </div>

              <div className="d-section">
                <SectionHeading id="wq-not-trigger">If it does not trigger</SectionHeading>
                <ul>
                  <li>Repository URL in the platform matches GitHub's <code>repository.html_url</code> exactly - no <code>.git</code> suffix.</li>
                  <li>Webhook secret matches on both sides.</li>
                  <li>Instance is running and the addon is not currently installing or updating.</li>
                  <li>The configured ref exists in the repository.</li>
                </ul>
                <p>If all of the above look correct, ask your platform admin to verify the addon source linkage.</p>
              </div>
            </>
          )}

          {active === 'webhook-ref' && (
            <>
              <div className="d-page-header">
                <h1>GitHub Webhook - Reference</h1>
                <p>Full reference: DB-level source setup, native webhook, GitHub Actions fallback, signature testing, and all API responses.</p>
                <button type="button" className="d-copy-page" onClick={onCopyPage}>{pageCopied ? '✓ Copied' : '⎘ Copy for AI'}</button>
              </div>

              <div className="d-section">
                <SectionHeading id="wr-how">How it works</SectionHeading>
                <ol>
                  <li>The API validates <code>X-Hub-Signature-256</code> against the source <code>webhook_secret</code>.</li>
                  <li>It finds the matching source row by <code>repository.html_url</code>.</li>
                  <li>It finds all addons linked via <code>addons.source_id</code>.</li>
                  <li>It downloads the repository at the configured <code>ref</code>.</li>
                  <li>It replaces addon files and queues update jobs for all running instances.</li>
                </ol>
                <p>Supported events: <code>push</code>, <code>release</code>, <code>ping</code> (health check only).</p>
              </div>

              <div className="d-section">
                <SectionHeading id="wr-step1">Step 1 - Link the addon source</SectionHeading>
                <h3>Get the addon ID</h3>
                <CodeBlock title="List addons" code={`curl -sS "$BASE_URL/api/v1/instances/$INSTANCE_ID/addons" \\\n  -H "Authorization: Bearer $TOKEN" | jq`} id="list-addons" />
                <h3>Insert source and link addon</h3>
                <CodeBlock title="Run from platform/ directory" code={dbSetup} id="db-setup" />
                <div className="d-note"><code>repo_url</code> must exactly match GitHub's <code>repository.html_url</code> - no <code>.git</code> suffix, case-sensitive.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="wr-step2">Step 2 - Configure delivery</SectionHeading>
                <h3>Option A - Native GitHub webhook (recommended)</h3>
                <p>Go to <strong>Settings → Webhooks → Add webhook</strong> in the repository:</p>
                <table className="d-table">
                  <thead><tr><th>Field</th><th>Value</th></tr></thead>
                  <tbody>
                    <tr><td>Payload URL</td><td>{webhookUrl}</td></tr>
                    <tr><td>Content type</td><td>application/json</td></tr>
                    <tr><td>Secret</td><td>The WEBHOOK_SECRET from Step 1</td></tr>
                    <tr><td>Events</td><td>Just the push event (add release if needed)</td></tr>
                  </tbody>
                </table>

                <h3>Option B - GitHub Actions</h3>
                <p>Create <code>.github/workflows/oaas-webhook.yml</code> and add secrets <code>OAAS_WEBHOOK_URL</code> and <code>OAAS_WEBHOOK_SECRET</code> to the repository.</p>
                <CodeBlock title=".github/workflows/oaas-webhook.yml" code={actionYaml} id="action-yaml" />
              </div>

              <div className="d-section">
                <SectionHeading id="wr-testing">Testing</SectionHeading>
                <CodeBlock title="Send a signed test request" code={curlTest} id="curl-test" />
              </div>

              <div className="d-section">
                <SectionHeading id="wr-responses">API responses</SectionHeading>
                <table className="d-table">
                  <thead><tr><th>Response</th><th>Meaning</th></tr></thead>
                  <tbody>
                    <tr><td>{'{"status":"pong"}'}</td><td>Ping accepted - endpoint is reachable.</td></tr>
                    <tr><td>{'{"status":"ignored"}'}</td><td>Event type is not push or release.</td></tr>
                    <tr><td>{'{"status":"no_source_configured"}'}</td><td>No source row matches the repository.html_url.</td></tr>
                    <tr><td>{'{"status":"no_addons_linked"}'}</td><td>Source exists but no addon has source_id set.</td></tr>
                    <tr><td>{'{"error":"invalid signature"}'}</td><td>HMAC secret mismatch.</td></tr>
                  </tbody>
                </table>
              </div>

              <div className="d-section">
                <SectionHeading id="wr-troubleshooting">Troubleshooting</SectionHeading>
                <table className="d-table">
                  <thead><tr><th>Problem</th><th>Fix</th></tr></thead>
                  <tbody>
                    <tr><td>no_source_configured</td><td><code>repo_url</code> must match <code>repository.html_url</code> exactly - check trailing slashes and <code>.git</code> suffix.</td></tr>
                    <tr><td>invalid signature</td><td>The <code>webhook_secret</code> in <code>addon_sources</code> doesn't match the signing secret. Update the DB row or regenerate.</td></tr>
                    <tr><td>no_addons_linked</td><td>Source row exists but <code>addons.source_id</code> was not set. Re-run the UPDATE query from Step 1.</td></tr>
                    <tr><td>Jobs not appearing</td><td>Instance must be running and the addon must not be mid-install.</td></tr>
                  </tbody>
                </table>
              </div>
            </>
          )}

          {active === 'filestore-recovery' && (
            <>
              <div className="d-page-header">
                <h1>Filestore recovery</h1>
                <p>Export the Odoo filestore from a running environment and upload it to fix a missing or mismatched filestore after a restore.</p>
                <button type="button" className="d-copy-page" onClick={onCopyPage}>{pageCopied ? '✓ Copied' : '⎘ Copy for AI'}</button>
              </div>

              <div className="d-section">
                <SectionHeading id="fs-when">When to use this</SectionHeading>
                <p>After a database restore, Odoo may return 500 errors for pages that load attachments (images, CSS bundles, company logo). This happens when the database references files that are not present in the filestore volume. Common causes:</p>
                <ul>
                  <li>The backup was created before filestore archiving was supported - only the database dump was restored.</li>
                  <li>The database was restored from a different source instance and the filestores were never transferred.</li>
                </ul>
                <p>The fix is to export the correct filestore from its original location and upload it via <strong>Replace Filestore</strong> in the Backups tab.</p>
              </div>

              <div className="d-section">
                <SectionHeading id="fs-export">Export the filestore</SectionHeading>
                <p>Odoo stores attachments at <code>/var/lib/odoo/filestore/&lt;subdomain&gt;/</code> inside the Odoo container, which maps to a named volume. The archive must contain the filestore contents directly - no subdirectory wrapper.</p>

                <h3>From a named volume (recommended)</h3>
                <p>First, find the volume name:</p>
                <CodeBlock title="List relevant volumes" code={fsListVolumes} id="fs-list-volumes" />
                <p>Then export from it using a one-shot container:</p>
                <CodeBlock title="Export from volume" code={fsDumpVolume} id="fs-dump-volume" />
                <p>Replace <code>&lt;odoo-volume&gt;</code> with the volume name and <code>&lt;subdomain&gt;</code> with the Odoo database name (same as the instance subdomain).</p>

                <h3>From a directory on disk</h3>
                <p>If the filestore is bind-mounted to a directory on the host rather than a named volume:</p>
                <CodeBlock title="Export from directory" code={fsDumpDirectory} id="fs-dump-directory" />
                <div className="d-note">The <code>-C</code> flag changes into the directory before archiving, so paths inside the archive start with <code>./</code> rather than the full path. This is required for the upload to work correctly.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="fs-verify">Verify the archive</SectionHeading>
                <p>Before uploading, confirm the archive structure is correct:</p>
                <CodeBlock title="Check archive contents" code={fsVerify} id="fs-verify-cmd" />
                <p>Paths must start with <code>./</code>. If you see <code>filestore/&lt;subdomain&gt;/…</code> at the top level the archive was created from the wrong working directory and the upload will produce an empty filestore.</p>
              </div>

              <div className="d-section">
                <SectionHeading id="fs-upload">Upload via the platform</SectionHeading>
                <ol>
                  <li>Open the instance, go to the <strong>Backups</strong> tab.</li>
                  <li>Scroll to <strong>Replace Filestore</strong> at the bottom of the tab.</li>
                  <li>Click <strong>Upload filestore archive</strong> and select <code>filestore.tar.gz</code>.</li>
                  <li>Status changes to <strong>Replacing Filestore</strong> - the instance stops briefly then restarts.</li>
                </ol>
                <div className="d-warn">This overwrites the entire filestore. Any files added to the instance after the exported snapshot will be lost.</div>
              </div>

              <div className="d-section">
                <SectionHeading id="fs-troubleshooting">Troubleshooting</SectionHeading>
                <table className="d-table">
                  <thead><tr><th>Problem</th><th>Fix</th></tr></thead>
                  <tbody>
                    <tr><td>Upload rejected</td><td>File must end in <code>.tar.gz</code>. Max 5 GB.</td></tr>
                    <tr><td>500 errors persist after replace</td><td>Verify archive structure - paths must start with <code>./</code>, not <code>filestore/…</code>. Re-export with the correct <code>-C</code> flag.</td></tr>
                    <tr><td>Wrong files after replace</td><td>The subdomain used in the export path must match the instance's database name exactly. Check with <code>tar tzf filestore.tar.gz | head</code>.</td></tr>
                    <tr><td>Button disabled</td><td>Instance must be <strong>Running</strong> or <strong>Stopped</strong>, and plan must be dev or pro.</td></tr>
                  </tbody>
                </table>
              </div>
            </>
          )}

        </main>
      </div>
    </div>
  )
}
